// Account-data export helper.
//
// Wraps `accountApi.exportAccountData()` (which returns a base64 ZIP archive
// by default) in the platform-specific "save / share" flow used elsewhere
// in the app. On web we trigger a Blob download; on native we write the
// bytes to the FileSystem cache and hand the file to the OS share sheet.
//
// Mirrors the pattern in `constants/utils/conversationExport.js` so the
// "Download my data" UX feels identical to "Export this conversation".

import { Platform } from 'react-native';
import * as FileSystem from 'expo-file-system';
import * as Sharing from 'expo-sharing';
import { accountApi } from '@/lib/api';

const ZIP_MIME_TYPE = 'application/zip';

function buildFilename() {
  const datestamp = new Date().toISOString().slice(0, 10);
  return `gab-ai-account-data-${datestamp}.zip`;
}

function downloadArchiveWeb(base64, filename) {
  // Decode in bounded chunks instead of allocating an archive-sized binary
  // string in addition to the base64 response and Blob.
  const parts = [];
  for (let offset = 0; offset < base64.length; offset += 64 * 1024) {
    const binary = atob(base64.slice(offset, offset + 64 * 1024));
    parts.push(Uint8Array.from(binary, char => char.charCodeAt(0)));
  }
  const blob = new Blob(parts, { type: ZIP_MIME_TYPE });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  // Give the browser time to start reading the download before releasing it.
  setTimeout(() => window.URL.revokeObjectURL(url), 60_000);
}

async function shareFileNative(base64, filename) {
  const fileUri = `${FileSystem.cacheDirectory}${filename}`;
  await FileSystem.writeAsStringAsync(fileUri, base64, {
    encoding: FileSystem.EncodingType.Base64,
  });

  const isAvailable = await Sharing.isAvailableAsync();
  if (!isAvailable) {
    throw new Error('Sharing is not available on this device');
  }

  await Sharing.shareAsync(fileUri, {
    mimeType: ZIP_MIME_TYPE,
    dialogTitle: 'Export account data',
    UTI: 'public.zip-archive',
  });

  // Best-effort cleanup — same window we use in conversationExport.
  setTimeout(async () => {
    try {
      await FileSystem.deleteAsync(fileUri, { idempotent: true });
    } catch (_) {
      // ignore cleanup failures
    }
  }, 60_000);
}

/**
 * Fetch the user's account data export as a ZIP archive and hand it to
 * the platform's native save/share flow. Throws on failure so callers
 * can surface a toast.
 *
 * @returns {Promise<{ filename: string }>} the chosen output filename,
 *   useful for follow-up confirmation toasts.
 */
export async function exportAccountData() {
  const { base64, filename: serverFilename } = await accountApi.exportAccountData({ format: 'zip' });

  // Prefer the server-suggested filename (which carries today's date in
  // the same shape as the rest of the codebase) and fall back to a
  // locally-generated one if a proxy stripped the header.
  const filename = serverFilename || buildFilename();

  if (Platform.OS === 'web') {
    downloadArchiveWeb(base64, filename);
  } else {
    await shareFileNative(base64, filename);
  }

  return { filename };
}

export default exportAccountData;
